/* eslint-disable react/jsx-handler-names */
import React from 'react'
import classNames from 'classnames'

import events, { Event } from 'pubsub/events'
import { DropdownTrigger } from '../DropdownMenu/DropdownTrigger'
import { NavMenuButton } from './NavMenuButton/NavMenuButton'
import { useSelector } from 'react-redux'
import { getUserLoggedInState } from '../../redux/reducers/users'
import { useTranslation } from 'react-i18next'
import { getOpenModal } from 'redux/reducers/header'

export type NavItem = {
  label: string;
  active?: boolean;
  className?: string;
  badge?: React.ReactNode;
  onClick?: () => void;
  dropdown?: React.ReactNode;
  href?: string;
  icon?: string;
  hasAccordionCaret?: boolean;
  locationUrls?: Array<string>;
  gaText?: string;
  labelHasPotentialPii?: boolean;
};

export interface NavMenuProps {
  analyticsCategory: string;
  className?: string;
  items: NavItem[];
  ariaLabel: string;
}

export const NavMenu: React.FC<NavMenuProps> = ({ analyticsCategory, className, items, ariaLabel }) => {
  const navClassName = classNames(`ch-c-navmenu`, className)
  const isLoggedIn = useSelector(getUserLoggedInState)

  const currentModalOpen = useSelector(getOpenModal)
  const { t, i18n } = useTranslation('common')

  const createClickHandler = (item: NavItem) => (isActive: boolean) => {
    const options: Event = {
      authenticated: isLoggedIn,
      text: item.gaText || item.label,
      targetLocation: item.href,
      category: analyticsCategory,
      activity: 'Link Click',
      url: window.location.href,
      language: i18n.language,
      title: document.title,
      sendToTealium: true,
      sendToCoreAPI: true,
      isMessageCenter: false
    }
    if (!isActive) { // dropdown was opened
      events.publish(options)
    } else if (item.label === 'Log out' || item.label === 'Log in') { // isActive is true for login/logout button in items
      events.publish(options)
    }

    if (item.onClick) {
      item.onClick()
    }
  }

  return (
    <nav aria-label={t(ariaLabel)} className={navClassName}>
      <ul className='ch-u-no-ul-styles ds-u-display--flex'>
        {items.map(({ hasAccordionCaret = false, locationUrls = [], ...item }, index) => {
          const buttonClassName = classNames(
            // underlines are applied by the nav-menu-button-content-container in dropdown trigger buttons
            item.dropdown ? 'ch-u-text-hover--none ch-u-text-focus--none' : undefined,
            item.labelHasPotentialPii ? 'qm-block-pii' : undefined
          )

          return (
            <li className={item.className || ''} key={`${index}-${item.label}`}>
              {item.dropdown
                ? (
                  <DropdownTrigger onClick={createClickHandler(item)} dropdown={item.dropdown}>
                    <NavMenuButton
                      active={item.active || currentModalOpen === item.label}
                      label={item.label}
                      hasAccordionCaret={hasAccordionCaret}
                      icon={item.icon}
                      className={buttonClassName}
                      locationUrls={locationUrls}
                      badge={item.badge}
                      hasDropdown
                    />
                  </DropdownTrigger>
                  )
                : (
                  <NavMenuButton
                    onClick={createClickHandler(item)}
                    href={item.href}
                    active={item.active || currentModalOpen === item.label}
                    label={item.label}
                    className={buttonClassName}
                    icon={item.icon}
                    locationUrls={locationUrls}
                    badge={item.badge}
                  />
                  )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
